import { useState, useEffect, useCallback, useRef } from 'react';
import { getPatients, getPatientsByStatus } from '../services/api/apis';
import type { Patient } from '../types';

export type PatientFilter = 'all' | 'pending' | 'assigned' | 'submitted' | 'refunded' | 'issued' | 'rejected';

interface UsePatientListReturn {
  pageItems: Patient[];
  pageSize: number;
  pageIndex: number;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  canGoPrev: boolean;
  goNext: () => void;
  goPrev: () => void;
  changePageSize: (n: number) => void;
  refresh: () => void;
}

export const usePatientList = (filter: PatientFilter = 'all'): UsePatientListReturn => {
  const [pageItems, setPageItems] = useState<Patient[]>([]);
  const [pageSize, setPageSize] = useState(50);
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  // Both endpoints return the same paginated shape — one fetch function handles all filters.
  const fetchPage = useCallback(async (cursor: string | null | undefined, size: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = filter === 'all'
        ? await getPatients({ limit: size, cursor: cursor ?? null })
        : await getPatientsByStatus(filter, { limit: size, cursor: cursor ?? null });
      setPageItems(resp.data);
      setNextCursor(resp.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load patients');
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  const loadFirstPage = useCallback((size = pageSizeRef.current) => {
    setCursors([null]);
    setPageIndex(1);
    fetchPage(null, size);
  }, [fetchPage]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const goNext = useCallback(() => {
    if (isLoading || nextCursor === null) return;
    const c = nextCursor;
    setCursors(prev => [...prev, c]);
    setPageIndex(prev => prev + 1);
    fetchPage(c, pageSizeRef.current);
  }, [isLoading, nextCursor, fetchPage]);

  const goPrev = useCallback(() => {
    if (isLoading || pageIndex <= 1) return;
    const newCursors = cursors.slice(0, -1);
    const prevCursor = newCursors[newCursors.length - 1];
    setCursors(newCursors);
    setPageIndex(prev => prev - 1);
    fetchPage(prevCursor, pageSizeRef.current);
  }, [isLoading, pageIndex, cursors, fetchPage]);

  const changePageSize = useCallback((n: number) => {
    setPageSize(n);
    pageSizeRef.current = n;
    loadFirstPage(n);
  }, [loadFirstPage]);

  return {
    pageItems,
    pageSize,
    pageIndex,
    isLoading,
    error,
    hasMore: nextCursor !== null,
    canGoPrev: pageIndex > 1,
    goNext,
    goPrev,
    changePageSize,
    refresh: () => loadFirstPage(),
  };
};
